import { PrintDocument } from '@/components/ui/PrintDocument';
import { env } from '@/config/env';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { formatAmountInWords, formatCurrency, formatDate } from '@/lib/format';
import type { FeeReceipt } from '@/types/fees';
import { FEE_METHOD_LABEL } from '../constants';

/** Who a receipt was issued to, as every fee screen already knows them. */
export interface ReceiptStudent {
  fullName: string;
  admissionNumber: string;
  /** Null at a college, which places nobody in a class or a section. */
  className: string | null;
  sectionName: string | null;
  rollNumber: string | null;
}

/** One receipt, with everything the paper has to name it by. */
export interface PrintableReceipt {
  receipt: FeeReceipt;
  student: ReceiptStudent;
  /** The session the fees were charged for — `2025-26`. */
  sessionName: string;
}

interface FeeReceiptDocumentProps extends PrintableReceipt {
  instituteName: string;
  instituteCity: string | null;
}

/** One line of the student block: a label and what it says. */
function Detail({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="py-0.5 pr-3 align-top font-semibold">{label}</dt>
      <dd className="py-0.5 pr-6">{value}</dd>
    </>
  );
}

/**
 * A fee receipt, laid out for paper.
 *
 * Never drawn on screen — the account and the day book already show all of
 * this, and a second copy on the page would be a second thing to keep in step.
 * It is plain by design: black on white, no cards, no rounded corners and no
 * colour that costs a school ink and says nothing in grey.
 *
 * The institute's own name and city head it, from branding rather than from
 * anything written into this file — the same build serves every school (§4).
 *
 * What it deliberately does *not* carry is the family's current balance. A
 * receipt is a record of one payment on one day; the balance moves afterwards,
 * and a reprint six months later that quoted today's figure would contradict
 * the copy the family was handed at the counter.
 */
export function FeeReceiptDocument({
  receipt,
  student,
  sessionName,
  instituteName,
  instituteCity,
}: FeeReceiptDocumentProps) {
  const tuition = Number(receipt.tuitionAmount);
  const books = Number(receipt.booksAmount);
  const other = Number(receipt.otherAmount);

  return (
    <article className="bg-white p-6 text-black max-w-2xl mx-auto [break-inside:avoid] [page-break-inside:avoid]">
      <header className="border-b-2 border-black pb-3 text-center">
        <h1 className="text-xl font-bold">{instituteName}</h1>
        {instituteCity && <p className="text-sm">{instituteCity}</p>}
        <p className="mt-2 text-sm font-semibold uppercase tracking-widest">Fee receipt</p>
      </header>

      {/* Said before the figures, not after them: somebody reading a cancelled
          receipt must know that before they read what it is for. */}
      {receipt.isCancelled && (
        <p className="mt-3 border border-black p-2 text-center text-sm font-bold uppercase tracking-widest">
          Cancelled — this receipt is no longer valid
        </p>
      )}

      <div className="mt-4 flex flex-wrap justify-between gap-x-6 gap-y-1 text-sm">
        <p>
          <span className="font-semibold">Receipt no.</span>{' '}
          <span className="font-mono">{receipt.receiptNumber}</span>
        </p>
        <p>
          <span className="font-semibold">Date</span> {formatDate(receipt.paidOn)}
        </p>
        <p>
          <span className="font-semibold">Session</span> {sessionName}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-[auto_1fr_auto_1fr] text-sm">
        <Detail label="Name" value={student.fullName} />
        <Detail label="Admission no." value={student.admissionNumber} />
        {student.className && (
          <Detail
            label="Class"
            value={[student.className, student.sectionName].filter(Boolean).join(' ')}
          />
        )}
        {student.rollNumber && <Detail label="Roll no." value={student.rollNumber} />}
      </dl>

      <table className="mt-5 w-full border-collapse text-sm">
        <thead>
          <tr className="border-y border-black text-left">
            <th className="py-2">Particulars</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {/* Only the heads this payment was actually split across. A row of
              zeroes reads as a charge that was waived rather than as one the
              family never paid against. */}
          {tuition > 0 && (
            <tr className="border-b border-black/20">
              <td className="py-1.5">Tuition fee</td>
              <td className="py-1.5 text-right font-mono">
                {formatCurrency(receipt.tuitionAmount)}
              </td>
            </tr>
          )}
          {books > 0 && (
            <tr className="border-b border-black/20">
              <td className="py-1.5">Books and materials</td>
              <td className="py-1.5 text-right font-mono">
                {formatCurrency(receipt.booksAmount)}
              </td>
            </tr>
          )}
          {other > 0 && (
            <tr className="border-b border-black/20">
              <td className="py-1.5">Other fees</td>
              <td className="py-1.5 text-right font-mono">
                {formatCurrency(receipt.otherAmount)}
              </td>
            </tr>
          )}
          <tr className="border-b-2 border-black font-bold">
            <td className="py-2">Total received</td>
            <td className="py-2 text-right font-mono">{formatCurrency(receipt.amount)}</td>
          </tr>
        </tbody>
      </table>

      <p className="mt-3 text-sm">
        <span className="font-semibold">In words</span> {formatAmountInWords(receipt.amount)}
      </p>

      <p className="mt-1 text-sm">
        <span className="font-semibold">Paid by</span> {FEE_METHOD_LABEL[receipt.method]}
        {receipt.reference && ` · ${receipt.reference}`}
      </p>

      {receipt.remarks && (
        <p className="mt-1 text-sm">
          <span className="font-semibold">Remarks</span> {receipt.remarks}
        </p>
      )}

      {receipt.isCancelled && (
        <p className="mt-1 text-sm">
          <span className="font-semibold">Cancelled by</span> {receipt.cancelledBy}
          {receipt.cancellationReason && ` — ${receipt.cancellationReason}`}
        </p>
      )}

      <div className="mt-12 flex items-end justify-between text-sm">
        <span>Received by {receipt.collectedBy}</span>
        <span className="border-t border-black pt-1">Signature and seal</span>
      </div>

      <p className="mt-6 text-center text-xs">
        Computer generated receipt. Please keep it for your records.
      </p>
    </article>
  );
}

export interface FeeReceiptPrintProps {
  /** The receipt to print, or null when nothing is being printed. */
  target: PrintableReceipt | null;
  onFinished: () => void;
}

/**
 * Sends one receipt to the printer, from wherever it was asked for.
 *
 * Both the account dialog and the day book mount this, so a receipt looks the
 * same whichever screen it was printed from — and neither of them has to know
 * how printing works.
 */
export function FeeReceiptPrint({ target, onFinished }: FeeReceiptPrintProps) {
  const branding = useInstituteBranding((state) => state.branding);

  return (
    <PrintDocument
      isOpen={target !== null}
      onFinished={onFinished}
      fileName={target ? `Receipt-${target.receipt.receiptNumber}` : 'Receipt'}
    >
      {target && (
        <FeeReceiptDocument
          {...target}
          // The product name stands in only if branding has not loaded — the
          // paper must never go out headed with another institute's name.
          instituteName={branding?.displayName ?? env.appName}
          instituteCity={branding?.city ?? null}
        />
      )}
    </PrintDocument>
  );
}
